import axios from "axios";
import { getSubdomainForApi } from "../utils/portalHost";
import { getApiBase } from "../utils/apiBase";
import { attachApiErrorMessage } from "../utils/apiError";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  hasAccessToken,
} from "../utils/authStorage";

const API_BASE = getApiBase();

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (!config.headers) config.headers = {};
  const tenantSub = getSubdomainForApi();
  if (tenantSub && !shouldSkipTenantHeader(config)) {
    config.headers["X-Tenant"] = tenantSub;
  }
  const token = getAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const isFormData =
    typeof FormData !== "undefined" && config?.data instanceof FormData;
  if (!isFormData) return config;

  try {
    delete config.headers["Content-Type"];
    delete config.headers["content-type"];
  } catch (_) {}
  return config;
});

let isRefreshing = false;
let failedRequests = [];
let onUnauthorized = null;
let onSubscriptionRequired = null;

const AUTH_SKIP_REFRESH = [
  "/auth/login",
  "/auth/owner-login",
  "/auth/platform-login",
  "/auth/register",
  "/auth/register/send-phone-code",
  "/auth/register/verify-phone-code",
  "/auth/register-patient",
  "/auth/refresh-token",
  "/auth/logout",
  "/auth/platform-logout",
  "/auth/me",
  "/auth/platform-me",
  "/partners/",
];

const AUTH_SKIP_TENANT = [
  "/auth/register",
  "/auth/register/send-phone-code",
  "/auth/register/verify-phone-code",
  "/auth/owner-login",
  "/auth/platform-login",
  "/auth/platform-me",
  "/auth/platform-logout",
  "/platform/",
  "/owner/",
  "/partners/",
];

function shouldSkipAuthRefresh(config) {
  if (!config || config.skipAuthRefresh) return true;
  const url = String(config.url || "");
  return AUTH_SKIP_REFRESH.some((path) => url.includes(path));
}

function shouldSkipTenantHeader(config) {
  if (!config || config.skipTenant) return true;
  const url = String(config.url || "");
  return AUTH_SKIP_TENANT.some((path) => url.includes(path));
}

function flushFailedRequests(err) {
  const queue = failedRequests;
  failedRequests = [];
  queue.forEach(({ resolve, reject, config }) => {
    if (err) reject(err);
    else resolve(api(config));
  });
}

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

export function setSubscriptionRequiredHandler(handler) {
  onSubscriptionRequired = handler;
}

export { setAccessToken, clearAccessToken, hasAccessToken, getAccessToken };

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !shouldSkipAuthRefresh(originalRequest)
    ) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshRes = await api.post("/auth/refresh-token", null, {
            skipAuthRefresh: true,
          });
          if (refreshRes.data?.accessToken) {
            setAccessToken(refreshRes.data.accessToken);
          }
          flushFailedRequests(null);
          return api(originalRequest);
        } catch (refreshError) {
          flushFailedRequests(refreshError);
          clearAccessToken();
          if (onUnauthorized) onUnauthorized();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve, reject) => {
        failedRequests.push({ resolve, reject, config: originalRequest });
      });
    }

    if (error.response?.status === 402) {
      const code = error.response?.data?.code;
      if (
        (code === "subscription_required" || code === "club_suspended") &&
        typeof onSubscriptionRequired === "function"
      ) {
        try {
          onSubscriptionRequired(error.response.data);
        } catch (_) {}
      }
      return Promise.reject(attachApiErrorMessage(error));
    }

    return Promise.reject(attachApiErrorMessage(error));
  },
);